import {
  Box,
  HStack,
  Icon,
  Progress,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
} from '@chakra-ui/react';
import { GeocodingFeature } from '@mapbox/search-js-core';
import { GripHorizontal, GripVertical } from 'lucide-react';
import { FC, useMemo, useRef, useState } from 'react';
import { MapRef } from 'react-map-gl';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Navigate } from 'react-router';
import { ComingSoon } from '~/apps/consumer/components/ComingSoon';
import { ZipCodesGeoMap } from '~/apps/consumer/components/Map/ZipCodesGeoMap/ZipCodesGeoMap';
import {
  DEFAULT_POSITION,
  MLS_TO_BBOX_MAP,
} from '~/apps/consumer/components/MapSearch/constants';
import { TeamPerformanceNotReady } from '~/apps/consumer/components/ProductFeatures/TeamPerformanceNotReady';
import {
  FeatureKey,
  useIsFeatureEnabledForPayload,
} from '~/apps/consumer/hooks/useIsFeatureEnabledForPayload';
import { bboxToLngLatBounds } from '~/apps/consumer/utils/map.utils';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { Filters } from './components/Filters';
import { PerformanceList } from './components/PerformanceList';
import { TeamPerformancePanel } from './components/TeamPerformancePanel';
import { TopPerformers } from './components/TopPerformers';
import { useOrgPerformance } from './hooks/useOrgPerformance';
import { usePanel } from './hooks/usePanel';
import { usePerformanceMetrics } from './hooks/usePerformanceMetrics';

export const OrganizationPerformancePage: FC = () => {
  const {
    selectedOrganization,
    loadingOrganizations,
    loadingIsSelectedOrgAdmin,
  } = useAppContext();

  // Uses the offer-coach feature flag
  const isOfferScorecardEnabled = useIsFeatureEnabledForPayload({
    featureFlagKey: FeatureKey.OFFER_COACH,
    organizationId: selectedOrganization?.id,
  });

  const mapRef = useRef<MapRef>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mapSuggestions, setMapSuggestions] = useState<GeocodingFeature[]>([]);
  const { panelMinSizes, isRightPanelLg, onLayout, isMobile } =
    usePanel(mapRef);

  const {
    zipCodesGeoJson,
    allTransactions,
    handleMapClick,
    selectedZipCodes,
    setSelectedZipCodes,
    transactionFilters,
    setTransactionFilters,
    perAgentOfferAcceptRateData,
    perAgentTransactionStatsData,
    perAgentTransactionStatsDataBySelectedZipCodes,
    perAgentOfferAcceptRateDataBySelectedZipCodes,
    perOfficesTransactionStatsData,
    perOfficesOfferAcceptRateData,
    isLoading,
  } = useOrgPerformance(mapSuggestions);

  const filteredTransactions = useMemo(
    () =>
      allTransactions?.filter(
        (transaction) =>
          selectedZipCodes.length === 0 ||
          selectedZipCodes.includes(transaction.zip_code)
      ),
    [allTransactions, selectedZipCodes]
  );

  const { teamPerformance, topPerformers } = usePerformanceMetrics(
    filteredTransactions,
    selectedZipCodes.length
      ? perAgentOfferAcceptRateDataBySelectedZipCodes?.perAgentOfferAcceptRate
      : perAgentOfferAcceptRateData?.perAgentOfferAcceptRate,
    selectedZipCodes.length
      ? perAgentTransactionStatsDataBySelectedZipCodes?.perAgentTransactionStats
      : perAgentTransactionStatsData?.perAgentTransactionStats
  );

  const mainMLSOSN = selectedOrganization?.mainMLS?.originatingSystemName;
  const isNotReady = selectedOrganization?.contractSystemCredentials?.length
    ? selectedOrganization?.contractSystemCredentials?.every(
        (cred) => !cred.isReady
      )
    : window.ENVS.environment !== 'development';

  if (loadingOrganizations || loadingIsSelectedOrgAdmin) {
    return <CenterSpinner />;
  }

  if (isOfferScorecardEnabled === false) {
    return <Navigate replace to="/" />;
  }

  if (isNotReady) {
    return <TeamPerformanceNotReady />;
  }

  return (
    <PanelGroup
      direction={isMobile ? 'vertical' : 'horizontal'}
      onLayout={onLayout}
    >
      <Panel
        defaultSize={Math.floor(100 - panelMinSizes[1])}
        minSize={panelMinSizes[0]}
      >
        <Box
          flexGrow={{ base: 0, md: 1 }}
          flexShrink={{ base: 1, md: 0 }}
          height="100%"
          position="relative"
          width="100%"
        >
          {isLoading && (
            <Progress
              isIndeterminate
              left={0}
              position="absolute"
              right={0}
              size="xs"
              top={0}
              zIndex={1}
            />
          )}
          {!zipCodesGeoJson && isLoading && (
            <HStack
              alignItems="center"
              bottom={0}
              justifyContent="center"
              left={0}
              position="absolute"
              right={0}
              top={0}
              zIndex={1}
            >
              <Box bgColor="blackAlpha.800" p={4} rounded="md">
                <CenterSpinner />
              </Box>
            </HStack>
          )}

          <ZipCodesGeoMap
            handleMapClick={handleMapClick}
            initialViewState={
              mainMLSOSN && MLS_TO_BBOX_MAP[mainMLSOSN]
                ? {
                    bounds: bboxToLngLatBounds(MLS_TO_BBOX_MAP[mainMLSOSN]),
                  }
                : {
                    bounds: bboxToLngLatBounds(DEFAULT_POSITION.bbox),
                  }
            }
            mapRef={mapRef}
            setMapSuggestions={setMapSuggestions}
            zipCodesGeoJson={zipCodesGeoJson}
          />
        </Box>
      </Panel>
      <PanelResizeHandle
        style={{
          position: 'relative',
          width: isMobile ? '100%' : '10px',
          height: isMobile ? '10px' : '100%',
        }}
      >
        <Box
          bottom={isMobile ? '0' : '-20px'}
          left={isMobile ? '50%' : '-8px'}
          position="absolute"
          top={isMobile ? '-10px' : '50%'}
        >
          <Box bgColor="indigo.800" pt={1} px={1} rounded="sm">
            <Icon as={isMobile ? GripHorizontal : GripVertical} />
          </Box>
        </Box>
      </PanelResizeHandle>
      <Panel defaultSize={panelMinSizes[1]} minSize={panelMinSizes[1]}>
        <Box
          ref={scrollRef}
          bgColor="indigo.700"
          flexGrow={{ base: 0, md: 1 }}
          flexShrink={{ base: 1, md: 0 }}
          height={{ base: '100%', md: '100dvh' }}
          overflow="auto"
          width="100%"
        >
          <Tabs isLazy variant="document">
            <Box
              backdropFilter="blur(2px)"
              bgColor="indigo.700"
              pb={5}
              position={{ base: 'relative', md: 'sticky' }}
              top={0}
              zIndex={2}
            >
              <TabList bgColor="indigo.900" gap={0}>
                <Tab
                  borderColor="whiteAlpha.200"
                  borderRightWidth={1}
                  {...tabProps}
                >
                  Buying
                </Tab>
                <Tab {...tabProps} isDisabled>
                  Listing
                  <ComingSoon
                    as="sup"
                    color="transparent"
                    fontSize="8px"
                    fontWeight="medium"
                    ml={1}
                    position="relative"
                    size={undefined}
                    textAlign="left"
                    textTransform="uppercase"
                    top="-0.5em"
                  />
                </Tab>
                <Tab {...tabProps} isDisabled>
                  Market Trends
                  <ComingSoon
                    as="sup"
                    color="transparent"
                    fontSize="8px"
                    fontWeight="medium"
                    ml={1}
                    position="relative"
                    size={undefined}
                    textAlign="left"
                    textTransform="uppercase"
                    top="-0.5em"
                  />
                </Tab>
              </TabList>

              <Filters
                selectedZipCodes={selectedZipCodes}
                setSelectedZipCodes={setSelectedZipCodes}
                setSuggestions={setMapSuggestions}
                setTransactionFilters={setTransactionFilters}
                suggestions={mapSuggestions}
                transactionFilters={transactionFilters}
              />
            </Box>

            <TabPanels>
              <TabPanel as={VStack} pt={1} px={0} spacing={4}>
                <Stack
                  alignItems="stretch"
                  flexDir={isRightPanelLg ? 'row' : 'column'}
                  px={4}
                  spacing={{ base: 4, lg: 6 }}
                  width="100%"
                >
                  <TeamPerformancePanel teamPerformance={teamPerformance} />
                  <TopPerformers
                    isLoading={isLoading}
                    topPerformers={topPerformers}
                  />
                </Stack>

                <Tabs
                  isLazy
                  variant="document"
                  width="100%"
                  onChange={() => {
                    window.setTimeout(() => {
                      window.dispatchEvent(new Event('resize'));
                    }, 100);
                  }}
                >
                  <TabList
                    borderBottom="1px solid"
                    borderColor="whiteAlpha.100"
                    gap={2}
                    px={4}
                  >
                    <Tab pb={4} px={2}>
                      Agents
                    </Tab>
                    {selectedOrganization?.hasDescendants && (
                      <Tab pb={4} px={2}>
                        Offices/Teams
                      </Tab>
                    )}
                  </TabList>

                  <TabPanels>
                    <TabPanel px={0}>
                      <PerformanceList
                        offerAcceptRate={
                          selectedZipCodes.length
                            ? perAgentOfferAcceptRateDataBySelectedZipCodes?.perAgentOfferAcceptRate
                            : perAgentOfferAcceptRateData?.perAgentOfferAcceptRate
                        }
                        scrollElement={scrollRef.current}
                        transactionStats={
                          selectedZipCodes.length
                            ? perAgentTransactionStatsDataBySelectedZipCodes?.perAgentTransactionStats
                            : perAgentTransactionStatsData?.perAgentTransactionStats
                        }
                        type="agent"
                      />
                    </TabPanel>
                    {selectedOrganization?.hasDescendants && (
                      <TabPanel px={0}>
                        <PerformanceList
                          offerAcceptRate={
                            perOfficesOfferAcceptRateData?.perOfficeOfferAcceptRate ??
                            []
                          }
                          scrollElement={scrollRef.current}
                          transactionStats={
                            perOfficesTransactionStatsData?.perOfficeTransactionStats ??
                            []
                          }
                          type="office"
                        />
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Panel>
    </PanelGroup>
  );
};

const tabProps = {
  _selected: {
    bg: 'indigo.700',
    color: 'white',
  },
  _disabled: {
    opacity: 0.9,
    cursor: 'default',
  },
  display: 'flex',
  alignItems: 'center',
  fontWeight: '500',
  px: { base: 3, md: 4 },
  py: { base: 2, md: 3 },
  lineHeight: '1',
};
